import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { MARKETING_LANGUAGES, findLanguage } from '@/lib/i18n/languages';
import { useLanguageMenu } from '@/components/chrome/useLanguageMenu';

// The dropdown loads on demand (see useLanguageMenu); a visually identical static button
// stands in until it arrives.
const TRIGGER_CLASS =
  'inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-1 text-xs font-medium text-foreground/80 transition hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface LanguageSelectorProps {
  availableLanguages?: string[];
}

export function LanguageSelector({ availableLanguages }: LanguageSelectorProps) {
  const { i18n } = useTranslation('common');

  // The marketing site offers English, Spanish and Brazilian Portuguese (see MARKETING_LANGUAGES).
  const languages = availableLanguages
    ? MARKETING_LANGUAGES.filter((lang) => availableLanguages.includes(lang.code))
    : MARKETING_LANGUAGES;

  // Auto-switch to the only available language if current language is not available
  useEffect(() => {
    if (availableLanguages && availableLanguages.length > 0) {
      const currentLangAvailable = availableLanguages.includes(i18n.language);
      if (!currentLangAvailable) {
        i18n.changeLanguage(availableLanguages[0]);
      }
    }
  }, [availableLanguages, i18n]);

  // Initialize i18n from persisted language choice on mount
  useEffect(() => {
    const stored = localStorage.getItem('cleancal_language');
    if (stored && stored !== i18n.language) {
      i18n.changeLanguage(stored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPicker = languages.length > 1;
  const { Menu, clickedEarly, placeholderProps } = useLanguageMenu(hasPicker);

  if (!hasPicker) {
    return null;
  }

  const currentLanguage = findLanguage(languages, i18n.language);

  const shortCode = currentLanguage.code.split('-')[0].toUpperCase();

  if (Menu) {
    return (
      <Menu
        languages={languages}
        currentLanguage={currentLanguage}
        shortCode={shortCode}
        triggerClassName={TRIGGER_CLASS}
        defaultOpen={clickedEarly}
      />
    );
  }

  return (
    <button type="button" className={TRIGGER_CLASS} {...placeholderProps}>
      <span className="text-sm leading-none">{currentLanguage.flag}</span>
      <span className="hidden sm:inline tabular-nums">{shortCode}</span>
      <span className="sr-only">Select language</span>
      <ChevronDown className="h-3 w-3 opacity-60" />
    </button>
  );
}
